import './BatchStatusPanel.css';

export interface BatchJob {
  id: string;
  created_at: string;
  scheduled_for: string;
  stage: string;
  word_ids: string[];
  status: 'pending' | 'running' | 'completed' | 'partial' | 'failed';
  results: Record<string, string>;
  completed_at?: string;
}

interface BatchStatusPanelProps {
  batches: BatchJob[];
  onCancel: (batchId: string) => void;
  onRefresh: () => void;
}

const STAGE_LABELS: Record<string, string> = {
  stage1_definition: 'Stage 1: Definition & Metadata',
  stage1b_fakes: 'Stage 1b: Fake Definitions',
  stage2_examples: 'Stage 2: Example Sentences',
  stage3_howtouse: 'Stage 3: How To Use',
  stage4_funfacts: 'Stage 4: Fun Facts',
  // stage7_social + stage9_relatedsocial retired 2026-07-05 (Social Engine).
  // stage6_reframes + stage8_related retired 2026-10-22 (nothing retired is made).
};

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getResultBreakdown(results: Record<string, string>): { success: number; fail: number } {
  let success = 0;
  let fail = 0;
  for (const val of Object.values(results)) {
    if (val === 'success') success++;
    else fail++;
  }
  return { success, fail };
}

export default function BatchStatusPanel({ batches, onCancel, onRefresh }: BatchStatusPanelProps) {
  const sorted = [...batches].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  return (
    <div className="batch-panel">
      <h3>
        Batch Jobs
        <button className="batch-refresh" onClick={onRefresh}>
          Refresh
        </button>
      </h3>

      {sorted.length === 0 ? (
        <p className="batch-empty">No batch jobs yet</p>
      ) : (
        <div className="batch-list">
          {sorted.map((job) => {
            const showResults =
              job.status === 'completed' || job.status === 'partial' || job.status === 'failed';
            const breakdown = showResults ? getResultBreakdown(job.results) : null;

            return (
              <div key={job.id} className="batch-item">
                <div className="batch-header">
                  <span className={`batch-badge batch-badge-${job.status}`}>{job.status}</span>
                  <span>{job.word_ids.length} words</span>
                  <span>{STAGE_LABELS[job.stage] ?? job.stage}</span>
                  {job.status === 'pending' && (
                    <button className="batch-cancel" onClick={() => onCancel(job.id)}>
                      Cancel
                    </button>
                  )}
                </div>
                <div className="batch-meta">Scheduled: {formatTime(job.scheduled_for)}</div>
                {breakdown && (
                  <div className="batch-results">
                    {breakdown.success} succeeded, {breakdown.fail} failed
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
